import type { ClmIdentity } from './claims';

/*
 * =============================================================================
 * READ THIS BEFORE USING ANYTHING IN THIS FILE
 *
 * These helpers are USABILITY ONLY. They are not access control and must never
 * be treated as such.
 *
 * NFR-06: "Authorisation enforced at the service layer, not the interface."
 * A hidden button is not access control. Every function here answers the
 * question "is it worth showing this to the user?" — never "is the user
 * allowed to do this?". The gateway API answers the second question, on every
 * request, from the bearer token, and returns 403 regardless of what the SPA
 * chose to render. If a check here is ever the only thing standing between a
 * user and an action, that is a defect in the API, not in this file.
 *
 * Consequently:
 *   - Prefer dimming/annotating over hiding. A surface the user cannot act on
 *     is still informative, and a nav item that silently vanishes generates a
 *     support ticket rather than an understanding.
 *   - Never call these to decide what to send. In particular the client must
 *     never derive a party/practice filter from `scope_groups` and put it in a
 *     request — CLM scope is resolved server-side from the token's group
 *     claims and is not a client-supplied parameter. src/api/client.ts rejects
 *     scope-shaped query parameters to keep that honest.
 * =============================================================================
 */

/**
 * Role names are intentionally NOT enumerated here.
 *
 * The definitive role taxonomy and its mapping to Entra app roles / security
 * groups is owned by PR-11 (role x scope resolution, `vw_effective_access`) and
 * is not yet ratified in docs/architecture.md — the only role-bearing group
 * named there is `CEQ-Legal` (§0.1 decision 15). Hardcoding a guessed enum of
 * role names in the SPA would create a second, wrong copy of an authorisation
 * model that has one home, server-side. So the shell treats roles as opaque
 * strings: it displays them, and it exposes a case-insensitive membership test
 * for a caller that already knows the name it cares about.
 *
 * CLM-82 — the test reads the UNION of the two role sources the identity
 * carries: the `roles` app-role claim and `roles_from_groups`, the roles the
 * SPA derives from the `groups` claim through the optional
 * `VITE_ENTRA_GROUP_CLM_*` bindings (CLM-65). cloudEQ grants CLM roles through
 * security groups, so in production the app-role claim is empty and the
 * group-derived list is the only one that can ever name a role client-side.
 */
function heldRoles(identity: ClmIdentity): readonly string[] {
  return [...identity.roles, ...identity.roles_from_groups];
}

export function hasRole(identity: ClmIdentity, role: string): boolean {
  const wanted = role.toLowerCase();
  return heldRoles(identity).some((held) => held.toLowerCase() === wanted);
}

export function hasAnyRole(identity: ClmIdentity, roles: readonly string[]): boolean {
  return roles.some((role) => hasRole(identity, role));
}

/**
 * Whether the SPA can name ANY role for this sign-in. False when the token
 * carries no app-role claim and no `VITE_ENTRA_GROUP_CLM_*` binding matched a
 * group — the production shape today, where the sidebar falls back to "Roles
 * resolved by the API" (claims.ts `roleTags`).
 */
export function roleHintAvailable(identity: ClmIdentity): boolean {
  return heldRoles(identity).length > 0;
}

/**
 * CLM-82 / D-82-1 — the display hint for a control the gateway grants to
 * `roles`. Three honest answers:
 *
 *   roles unknown             → true.  The SPA cannot name a role, so it must
 *                                       not disable: the control is enabled
 *                                       and the API's 403, which every surface
 *                                       already renders, is the answer.
 *   roles known, none match   → false. Dim the control and say which role it
 *                                       needs — the sign-in does show its
 *                                       roles and they are not enough.
 *   roles known, one matches  → true.
 *
 * Before this helper, a hint that read `hasAnyRole(...)` alone collapsed
 * "unknown" into "not allowed" and disabled every Re-run, Edit tags and
 * decision verb for everyone in production (14 Sep 2026). As everywhere in
 * this file: USABILITY ONLY, never access control (NFR-06).
 */
export function mayAttempt(identity: ClmIdentity, roles: readonly string[]): boolean {
  return !roleHintAvailable(identity) || hasAnyRole(identity, roles);
}

/**
 * How confidently the shell can describe the user's visible parties.
 *
 *   'unknown'   — not signed in, or the group claim overflowed. The shell says
 *                 "resolved by the API"; it does not say "none", which would be
 *                 wrong and alarming.
 *   'none'      — signed in, no groups: the claim is absent, or present and
 *                 empty. Not an empty result set — the API refuses every call
 *                 for this token (`no_groups_claim` when absent,
 *                 `no_clm_role_group` when empty) until it carries both a CLM
 *                 role group and a CLM scope group, so a scope group alone
 *                 fixes nothing. An absent claim can also mean the app
 *                 registration does not emit groups. Worth surfacing, because
 *                 NFR-05 group hygiene is a known 🟡 in docs/requirements.md.
 *   'scoped'    — one or more groups present. NOT "one or more CLM scope
 *                 groups": the claim mixes role, scope and unrelated groups,
 *                 and the SPA can name only the role groups (`role_groups`,
 *                 env.ts). A CEQ-CLM-Admin member with no CEQ-CLM-Scope-*
 *                 group is 'scoped' here and refused for having no scope by
 *                 the API (30 Sep 2026) — so nothing may present this as a
 *                 count of scope groups; TopBar says "groups in your sign-in".
 *                 The value keeps its name so the three states stay stable.
 */
export type ScopeConfidence = 'unknown' | 'none' | 'scoped';

export function scopeConfidence(identity: ClmIdentity, isAuthenticated: boolean): ScopeConfidence {
  if (!isAuthenticated || identity.scope_truncated) return 'unknown';
  return identity.scope_groups.length > 0 ? 'scoped' : 'none';
}

/**
 * Whether a nav item should be visually de-emphasised because the user's role
 * claims suggest they cannot act on it.
 *
 * `requiredRoles` empty (the default for every surface in this scaffold) means
 * "show normally" — the shell deliberately ships with no role gating at all
 * until PR-11 names the roles. When a later story does pass roles here, the
 * result must only ever drive styling, never routing: the route stays
 * reachable, the surface still loads, and the API is what refuses.
 */
export function navAffordance(
  identity: ClmIdentity,
  requiredRoles: readonly string[] | undefined,
): 'normal' | 'dimmed' {
  if (!requiredRoles || requiredRoles.length === 0) return 'normal';
  return hasAnyRole(identity, requiredRoles) ? 'normal' : 'dimmed';
}

/**
 * The Entra security group that carries the CLM `admin` role. The gateway maps
 * its object id via CLM_AUTH_GROUP_ADMIN (see packages/auth); the SPA knows only
 * the *name*, and only for the bulk-ingest planner's usability affordance
 * (originally CLM-43; widened by R-94-1 / CLM-94 — see `CLM_CONTRIBUTOR_GROUP_NAME`).
 */
export const CLM_ADMIN_GROUP_NAME = 'CEQ-CLM-Admin';

/**
 * The Entra security group that carries the CLM `contributor` role. Same
 * caveat as `CLM_ADMIN_GROUP_NAME` — named here only for the bulk-ingest
 * planner's usability affordance. R-94-1 (Gerry, 15 Sep 2026, CLM-94) moved
 * `contract:bulk_ingest` off Admin-only (CLM-43) onto `contributor`; admin
 * keeps it by inheritance in `packages/auth/src/roles.ts`.
 */
export const CLM_CONTRIBUTOR_GROUP_NAME = 'CEQ-CLM-Contributor';

/**
 * The Entra security group that carries the CLM `legal_reviewer` role —
 * `CEQ-Legal`, the one role-bearing group `docs/architecture.md` names (§0.1
 * decision 15; `packages/auth` maps its object id from `CLM_AUTH_GROUP_LEGAL`).
 * Named here only for the Admin rail's Base MSA entry (CLM-111), which Legal
 * reads (`clause:review_match`, D-2.1-11). Same caveat as
 * `CLM_ADMIN_GROUP_NAME`: the SPA knows the *name*, never the id.
 */
export const CLM_LEGAL_GROUP_NAME = 'CEQ-Legal';

/** An Entra object id — what the `groups` claim usually carries instead of names. */
const GUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether the bulk-ingest planner is worth showing to this user.
 *
 *   'eligible'     — the token names the contributor or admin role, or either's
 *                     Entra group.
 *   'not_eligible' — the token carries readable group names and neither group
 *                     is among them.
 *   'unknown'      — the SPA cannot tell: the group claim overflowed, or it
 *                     carries object ids (GUIDs) rather than names, which the
 *                     SPA has no mapping for. The API is the authority either
 *                     way.
 *
 * Tri-state on purpose. Collapsing 'unknown' into 'not_eligible' would hide the
 * planner from precisely the people most likely to hold many groups (claim
 * overflow) or to sit in a tenant that emits ids — the actively-misleading
 * readout claims.ts warns about. As everywhere in this file this is USABILITY
 * ONLY, never access control: the plan endpoint re-derives the caller's role
 * from the bearer token and answers 404 to anyone who does not hold
 * `contract:bulk_ingest` (NFR-06).
 *
 * Named for what it decides (planner visibility), not for which role currently
 * grants it — R-94-1 (CLM-94) already moved this once, from Admin alone to
 * Contributor-and-Admin, and the type should not need renaming if it moves
 * again.
 */
export type BulkIngestAffordance = 'eligible' | 'not_eligible' | 'unknown';

/**
 * The tri-state both admin-rail helpers share: `eligible` when the token names
 * one of `roles` (or one of `groups` as an app role, in case the registration
 * emits the group that way) or carries one of `groups` by name; `unknown` when
 * the claims cannot say; `not_eligible` only when readable group names prove
 * the absence.
 */
function claimAffordance(
  identity: ClmIdentity,
  isAuthenticated: boolean,
  roles: readonly string[],
  groups: readonly string[],
): BulkIngestAffordance {
  // App roles arrive as names by construction; accept either the role name the
  // gateway uses or the group name.
  if (hasAnyRole(identity, [...roles, ...groups])) return 'eligible';

  const wantedGroups = new Set(groups.map((group) => group.toLowerCase()));
  if (identity.scope_groups.some((group) => wantedGroups.has(group.toLowerCase()))) return 'eligible';

  if (!isAuthenticated || identity.scope_truncated) return 'unknown';

  // Every group value is GUID-shaped: the claim carries object ids, not names,
  // so the absence of the *name* proves nothing.
  if (
    identity.scope_groups.length > 0 &&
    identity.scope_groups.every((group) => GUID_SHAPE.test(group))
  ) {
    return 'unknown';
  }

  return 'not_eligible';
}

export function bulkIngestAffordance(
  identity: ClmIdentity,
  isAuthenticated: boolean,
): BulkIngestAffordance {
  return claimAffordance(
    identity,
    isAuthenticated,
    ['contributor', 'admin'],
    [CLM_CONTRIBUTOR_GROUP_NAME, CLM_ADMIN_GROUP_NAME],
  );
}

/**
 * CLM-107 — whether an Admin-rail entry gated on `admin:configure` is worth
 * showing: today, **Extraction accuracy** (`field-accuracy`, R-107-6).
 *
 * Narrower than {@link bulkIngestAffordance}: `admin:configure` is held by the
 * `admin` role alone — not by a contributor, and not by Legal
 * (`packages/auth/src/roles.ts`). So a contributor, who sees the Admin group
 * because of the planner, does not see this entry in it. Same three answers,
 * for the same reasons: `unknown` dims rather than hides, because hiding on an
 * overflowed or id-only claim would hide it from the admins most likely to
 * have one. USABILITY ONLY (NFR-06): the endpoint answers 403 to anyone
 * without `admin:configure`, and the route stays registered.
 */
export function adminConfigureAffordance(
  identity: ClmIdentity,
  isAuthenticated: boolean,
): BulkIngestAffordance {
  return claimAffordance(identity, isAuthenticated, ['admin'], [CLM_ADMIN_GROUP_NAME]);
}

/**
 * CLM-111 — whether an Admin-rail entry that Admin **and** Legal may read is
 * worth showing: today, **Base MSA** (`base-msa`, `list-base-contracts` on
 * `clause:review_match`, D-2.1-11 / R-111-6).
 *
 * Different in kind from the other two helpers: it is not narrower than the
 * group's own gate but *beside* it. Legal holds neither `contract:bulk_ingest`
 * nor `admin:configure`, so before CLM-111 a Legal-only sign-in never saw the
 * Admin group at all; with this entry the group renders whenever **any** of
 * its entries is not `not_eligible` (NavRail), and a Legal reviewer sees it
 * holding Base MSA alone. Same three answers, for the same reasons. USABILITY
 * ONLY (NFR-06): the list answers 403 to anyone without
 * `clause:review_match`, and the nominate write is `admin:configure`
 * whatever this says.
 */
export function adminOrLegalAffordance(
  identity: ClmIdentity,
  isAuthenticated: boolean,
): BulkIngestAffordance {
  return claimAffordance(
    identity,
    isAuthenticated,
    ['admin', 'legal_reviewer'],
    [CLM_ADMIN_GROUP_NAME, CLM_LEGAL_GROUP_NAME],
  );
}
